<template>
  <div class="content-wrapper view-aptlist-manage p-0 report-page">
    <div class="content">
      <div class="card">
        <div class="card-body">
          <h4><i class="fas fa-chart-bar"></i> รายงานรายได้แพทย์</h4>
          <div class="filter-card filter-card-v3">
            <div class="fcv3-primary fcv3-cols-2">
              <div class="field">
                <label class="field-lbl">ช่วงวันที่</label>
                <date-picker
                  v-model="dateRange"
                  type="date"
                  range
                  format="DD MMM YY"
                  :shortcuts="dateRangeShortcuts"
                  :disabled-date="notAfterToday"
                  placeholder="เลือกวันที่ต้องการค้นหา"
                  input-class="form-control form-control-sm field-input-native"
                ></date-picker>
              </div>

              <div class="field">
                <label class="field-lbl">แพทย์</label>
                <b-form-select
                  v-model="selectedDoctor"
                  :options="doctorsList"
                  text-field="fullName"
                  value-field="uid"
                  size="sm"
                  class="field-input-native"
                  :disabled="!isAccessMenu(['MANAGER', 'COUNTER', 'HEADCOUNTER'])"
                >
                  <template #first>
                    <b-form-select-option :value="null" disabled>
                      -- กรุณาเลือกแพทย์ --
                    </b-form-select-option>
                  </template>
                </b-form-select>
              </div>
            </div>

            <div class="fcv3-strip">
              <span class="fcv3-strip-lbl">หมวดหมู่</span>
              <button
                type="button"
                class="fcv3-all"
                :class="{ 'fcv3-all-active': allCatesSelected }"
                @click="toggleAllCates"
              >
                ทั้งหมด
              </button>
              <span class="fcv3-divider" aria-hidden="true"></span>
              <div class="fcv3-chips">
                <label
                  v-for="c in getTreatCateList"
                  :key="`t-${c.id}`"
                  class="chip fcv3-chip"
                  :class="{ 'chip-active': selectedCateIds.includes(c.id) }"
                >
                  <input type="checkbox" :value="c.id" v-model="selectedCateIds" />
                  <span>{{ c.nameTh }}</span>
                </label>
                <label
                  class="chip fcv3-chip fcv3-chip-ortho"
                  :class="{ 'chip-active': includeOrthoPkg }"
                >
                  <input type="checkbox" v-model="includeOrthoPkg" />
                  <span>แพ็กเกจจัดฟัน</span>
                </label>
                <label
                  v-for="c in getItemCateList"
                  :key="`i-${c.id}`"
                  class="chip fcv3-chip fcv3-chip-item"
                  :class="{ 'chip-active': selectedItemCateIds.includes(c.id) }"
                >
                  <input type="checkbox" :value="c.id" v-model="selectedItemCateIds" />
                  <span>{{ c.nameTh }}</span>
                </label>
              </div>
              <b-button
                variant="primary"
                class="btn-primary-lg fcv3-cta"
                @click="fetchAll"
              >
                แสดงข้อมูล
              </b-button>
            </div>
          </div>

          <div ref="fullscreenRoot" :class="{ 'my-fullscreen': isFullscreen }">
            <div class="my-fullscreen-bar">
              <span class="title">{{ reportTitle }}</span>
              <div class="meta">
                <span v-if="dateRange[0] && dateRange[1]">
                  วันที่: {{ formatDate(dateRange[0]) }} – {{ formatDate(dateRange[1]) }}
                </span>
                <span v-if="selectedDoctorName">
                  แพทย์: {{ selectedDoctorName }}
                </span>
                <span v-if="branchInfo && branchInfo.name">
                  สาขา: {{ branchInfo.name }}
                </span>
              </div>
              <span class="spacer"></span>
              <b-button
                size="sm"
                variant="outline-secondary"
                :title="fullscreenLabel"
                :aria-label="fullscreenLabel"
                @click="toggleFullscreen"
              >
                <i class="fas fa-times"></i>
              </b-button>
            </div>
            <div class="toolbar">
              <b-pagination
                v-model="currentPage"
                :total-rows="rows"
                :per-page="perPage"
                size="sm"
                first-number
                last-number
                pills
                limit="10"
                class="mb-0"
              ></b-pagination>

              <div class="toolbar-right">
                <b-dropdown size="sm" variant="outline-success" right>
                  <template #button-content>
                    <i class="fas fa-download"></i>
                  </template>
                  <b-dropdown-item @click="excelExport()">
                    <i class="fas fa-file-excel mr-2"></i>Excel
                  </b-dropdown-item>
                  <b-dropdown-item @click="pdfExport()">
                    <i class="fas fa-file-pdf mr-2"></i>PDF
                  </b-dropdown-item>
                </b-dropdown>
                <b-button
                  size="sm"
                  :variant="fullscreenButtonVariant"
                  :title="fullscreenLabel"
                  :aria-label="fullscreenLabel"
                  @click="toggleFullscreen"
                >
                  <i class="fas" :class="fullscreenIcon"></i>
                </b-button>
              </div>
            </div>
            <div class="pir-workspace">
              <div class="pir-main">
                <div class="pir-segmented" role="tablist" aria-label="มุมมองรายงาน">
                  <button
                    type="button"
                    class="pir-seg-btn"
                    :class="{ active: activeView === 'income' }"
                    @click="activeView = 'income'"
                  >
                    <i class="fas fa-receipt"></i>
                    <span>รายการรักษา</span>
                    <span class="pir-seg-ct">{{ incomeRowCount }}</span>
                  </button>
                  <button
                    type="button"
                    class="pir-seg-btn"
                    :class="{ active: activeView === 'lab' }"
                    @click="activeView = 'lab'"
                  >
                    <i class="fas fa-flask"></i>
                    <span>ค่าแลป</span>
                    <span class="pir-seg-ct">{{ labRowCount }}</span>
                  </button>
                  <button
                    type="button"
                    class="pir-seg-btn"
                    :class="{ active: activeView === 'dfp' }"
                    @click="activeView = 'dfp'"
                  >
                    <i class="fas fa-credit-card"></i>
                    <span>DF ตามวิธีชำระ</span>
                    <span class="pir-seg-ct">{{ paymentRowCount }}</span>
                  </button>
                  <button
                    type="button"
                    class="pir-seg-btn pir-seg-summary"
                    :class="{ active: activeView === 'summary' }"
                    @click="activeView = 'summary'"
                  >
                    <i class="fas fa-calculator"></i>
                    <span>สรุป</span>
                  </button>
                  <span class="pir-seg-spacer"></span>
                  <b-form-checkbox
                    class="pir-cf-switch"
                    v-model="reportSetting.cfVisible"
                    switch
                    @change="handleCfVisible"
                  >
                    แสดงส่วนแบ่งคลินิก (CF)
                  </b-form-checkbox>
                </div>

                <div v-show="activeView === 'income'" class="pir-card">
                  <div class="pir-view-toolbar">
                    <div class="seg" role="tablist" aria-label="มุมมอง">
                      <span class="seg-lbl">มุมมอง:</span>
                      <button
                        type="button"
                        class="seg-btn"
                        :class="{ active: incomeViewMode === 'financial' }"
                        @click="incomeViewMode = 'financial'"
                      >
                        💰 รายได้ &amp; DF/CF
                      </button>
                      <button
                        type="button"
                        class="seg-btn"
                        :class="{ active: incomeViewMode === 'payment' }"
                        @click="incomeViewMode = 'payment'"
                      >
                        💳 วิธีชำระ
                      </button>
                    </div>
                  </div>
                  <b-table
                    id="personalIncomeTable"
                    ref="personalIncomeTable"
                    class="my-0 sticky-footer report-table"
                    small
                    sort-icon-left
                    head-variant="light"
                    :busy="tableBusy"
                    :items="filteredPersonalIncomeList"
                    :fields="incomeVisibleFields"
                    sort-by="creationDt"
                    v-model="visibleRows"
                    :sort-desc="false"
                    :responsive="true"
                    show-empty
                    :sticky-header="tableHeight"
                    :per-page="perPage"
                    :current-page="currentPage"
                  >
                    <template #cell(index)="row">{{ row.item.row }}.</template>
                    <template #cell(creationDt)="row">
                      {{ formatDate(row.value) }}
                    </template>
                    <template #cell(treatType)="row">
                      {{ formatType(row.value) }}
                    </template>

                    <template #custom-foot>
                      <b-tr
                        v-if="incomeViewMode === 'financial'"
                        variant="secondary"
                        class="text-bold text-right"
                      >
                        <td class="text-left" :colspan="baseFields.length - 2">รวม</td>
                        <td>{{ sum("paid") }}</td>
                        <td></td>
                        <td v-if="hasAnyFee">{{ sum("processingFeeBaht") }}</td>
                        <td>{{ sum("paidDf") }}</td>
                        <td v-if="reportSetting.cfVisible">{{ sum("paidCf") }}</td>
                      </b-tr>
                      <b-tr
                        v-else
                        variant="secondary"
                        class="text-bold text-right"
                      >
                        <td class="text-left" :colspan="baseFields.length">รวม</td>
                        <td v-for="m in incomeDistinctMethods" :key="m.id">
                          {{ formatNumber(incomeMethodTotal(m.id)) }}
                        </td>
                      </b-tr>
                    </template>
                    <template #empty>
                      <p class="text-center text-muted my-2">
                        ไม่มีข้อมูลให้แสดง
                      </p>
                    </template>
                  </b-table>
                </div>

                <div v-show="activeView === 'lab'" class="pir-card pir-card-pad">
                  <DoctorLabExpeseTable :labReport="labReport" />
                </div>

                <div v-show="activeView === 'dfp'" class="pir-card pir-card-pad">
                  <PersonalDfByPaymentTypeReport
                    ref="dfByPaymentType"
                    :doctorUid="selectedDoctor"
                    :dateRange="dateRange"
                    :labReport="labReport"
                    :isDeducted="reportSetting.isDeducted"
                    :deductAmount="deductDfAmount"
                  />
                </div>

                <div v-show="activeView === 'summary'" class="pir-card pir-card-pad">
                  <DfSummaryTable
                    :total="total"
                    :reportSetting="reportSetting"
                    :dateRange="dateRange"
                    :dfByPaymentSummary="dfByPaymentSummary"
                    :summaryByPayment="summaryByPayment"
                    @updateIsDeducted="handleDeductChange"
                  />
                </div>
              </div>

              <aside class="pir-rail" aria-label="สรุปรายได้">
                <div class="pir-rail-card pir-rail-hero">
                  <div class="pir-rail-eyebrow">DF สุทธิที่จะได้รับ</div>
                  <div class="pir-rail-net">
                    <span class="pir-rail-net-num">{{ formatNumber(netDf) }}</span>
                    <span class="pir-rail-net-cur">฿</span>
                  </div>
                  <div class="pir-rail-net-sub">
                    หลังหักค่าแลป
                    <template v-if="isAfterCutoff">และค่าธรรมเนียม</template>
                  </div>
                </div>

                <div class="pir-rail-card">
                  <div class="pir-rail-card-title">การคำนวณ</div>
                  <div class="pir-rail-row">
                    <span>รายได้ DF</span>
                    <b class="df">{{ formatNumber(isAfterCutoff ? dfByPaymentSummary.df : total.paidDf) }}</b>
                  </div>
                  <template v-if="isAfterCutoff && dfByPaymentSummary.feeAmount">
                    <div class="pir-rail-row dim">
                      <span>− ค่าธรรมเนียม</span>
                      <b class="minus">−{{ formatNumber(dfByPaymentSummary.feeAmount) }}</b>
                    </div>
                    <div class="pir-rail-row">
                      <span>DF หลังหักค่าธรรมเนียม</span>
                      <b>{{ formatNumber(dfByPaymentSummary.netDf) }}</b>
                    </div>
                  </template>
                  <div class="pir-rail-row dim">
                    <span>− ค่าแลป</span>
                    <b class="minus">−{{ formatNumber(total.labDf) }}</b>
                  </div>
                  <div v-if="reportSetting.isDeducted" class="pir-rail-row dim">
                    <span>− หักค่าใช้จ่าย 10%</span>
                    <b class="minus">−{{ formatNumber(deductDfAmount) }}</b>
                  </div>
                  <div class="pir-rail-divider"></div>
                  <div class="pir-rail-row total">
                    <span>รับสุทธิ</span>
                    <b>{{ formatNumber(netDf) }}</b>
                  </div>
                  <div class="pir-rail-deduct">
                    <b-form-checkbox
                      v-model="reportSetting.isDeducted"
                      switch
                      @change="handleDeductChange"
                    >
                      หักค่าใช้จ่าย 10% ตามสัญญาแพทย์
                    </b-form-checkbox>
                  </div>
                </div>

                <div v-if="byPaymentBars.length" class="pir-rail-card">
                  <div class="pir-rail-card-title">DF ตามวิธีชำระ</div>
                  <div
                    v-for="(p, idx) in byPaymentBars"
                    :key="idx"
                    class="pir-rail-paybar"
                  >
                    <div class="pir-rail-paybar-head">
                      <span>{{ p.name }}</span>
                      <b>{{ formatNumber(p.df) }}</b>
                    </div>
                    <div class="pir-rail-paybar-track">
                      <div
                        class="pir-rail-paybar-fill"
                        :style="{ width: p.pct + '%' }"
                      ></div>
                    </div>
                  </div>
                </div>

                <div class="pir-rail-card pir-rail-meta">
                  <div>
                    <span>วันทำงาน</span>
                    <b>{{ workingDays }}</b>
                  </div>
                  <div>
                    <span>คนไข้</span>
                    <b>{{ uniquePatientCount }}</b>
                  </div>
                  <div>
                    <span>เฉลี่ย/วัน</span>
                    <b>{{ formatNumber(avgPerDay) }}</b>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </div>
      <!-- <div class="d-none">
        <div ref="header" id="printHeader">
          <h4>รายงานรายได้แพทย์</h4>
          <h6 v-if="selectedDoctorName">
            ทพ./ทพญ. {{ selectedDoctorName }}
            ตั้งแต่วันที่
            {{
              formatDate(dateRange.startDate) +
              "-" +
              formatDate(dateRange.endDate)
            }}
          </h6>
          <span class="text-right">
            พิมพ์วันที่ {{ formatDate(today) }} จัดทำโดย
            {{ userInfo.firstNameTh + " " + userInfo.lastNameTh }}</span
          >
        </div>
        <div ref="footer" id="printFooter">
          <h3>Footer</h3>
        </div>
      </div>-->
      <PdfExport ref="pdfExport" />
      <Dialog ref="Dialog"></Dialog>
      <Loading v-if="isLoading"></Loading>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

// import DateRangePicker from "vue2-daterange-picker";
// import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import DoctorLabExpeseTable from "@/components/reports/DoctorLabReport";
import DfSummaryTable from "@/components/reports/DfSummaryTable";
import PersonalDfByPaymentTypeReport from "@/components/reports/PersonalDfByPaymentTypeReport";

import PdfExport from "@/components/PdfExport";

import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import moment from "moment";
import { exportTableToExcel, exportReportToPdf } from "@/utils/reportExport";
import reportFullscreenMixin from "@/mixins/reportFullscreenMixin";

import { eventBus } from "../main";

export default {
  name: "PersonalIncomeReport",
  mixins: [reportFullscreenMixin],
  components: {
    DoctorLabExpeseTable,
    DfSummaryTable,
    PersonalDfByPaymentTypeReport,
    // DateRangePicker,
    PdfExport,
    Dialog,
    Loading,
  },
  data() {
    return {
      isLoading: false,

      // Doctors

      selectedDoctor: null,

      // Date range picker

      dateRange: [],

      //table
      tableBusy: false,
      personalIncomeList: [],
      labReport: [],
      baseFields: [
        { key: "index", label: "" },
        {
          key: "creationDt",
          label: "วันที่",
          sortable: true,
          class: "narrow-spacing",
        },
        { key: "dn", label: "DN.", class: "narrow-spacing" },
        {
          key: "patientFullName",
          label: "ชื่อคนไข้",
          class: "narrow-spacing",
        },
        { key: "itemName", label: "รายการรักษา", class: "narrow-spacing" },
        { key: "treatType", label: "ประเภท", class: "narrow-spacing" },
        { key: "price", label: "ค่ารักษา", class: "text-right" },
        { key: "finalDiscount", label: "ส่วนลด", class: "text-right" },
        { key: "amount", label: "ค่ารักษาสุทธิ", class: "text-right" },
        { key: "paid", label: "ชำระ", class: "text-right" },
        {
          key: "overdue",
          label: "ค้างชำระ",
          class: "text-right text-danger",
        },
      ],
      visibleRows: [],

      loadingTime: 0,
      selectedCateIds: [],
      selectedItemCateIds: [],
      includeOrthoPkg: false,
      feeMethods: [],

      total: {
        paid: 0,
        paidDf: 0,
        paidCf: 0,
        labTotal: 0,
        labDf: 0,
        labCf: 0,
      },

      // DF by payment type data
      dfByPaymentSummary: { df: 0, feeAmount: 0, netDf: 0 },
      summaryByPayment: [],

      //Excel Export
      tableList: [
        { id: "personalIncomeTable", sheetName: "รายได้แพทย์" },
        { id: "doctorLabReport", sheetName: "ค่าแลป" },
        { id: "PersonalDfTable", sheetName: "DF ตามวิธีชำระ" },
        { id: "PersonalDfSummaryByPayment", sheetName: "สรุปตามวิธีชำระ" },
      ],

      // pagination
      currentPage: 1,
      perPage: 100,

      tableTopPos: null,
      reportSetting: {
        cfVisible: false,
        isDeducted: false,
      },
      reportTitle: "รายงานรายได้แพทย์ (รายบุคคล)",
      reportNameEn: "MyIncome",

      activeView: "income", // 'income' | 'lab' | 'dfp' | 'summary'
      incomeViewMode: "financial", // 'financial' | 'payment'
      cutoffDate: new Date("2026-02-01"),
    };
  },
  created() {
    this.init();
  },
  computed: {
    ...mapGetters({
      doctorsList: "moduleUser/getDoctorList",
      isAccessMenu: "moduleUser/getIsAccessMenu",
      userInfo: "moduleUser/getUserInfo",
      getDoctorListBy: "moduleUser/getDoctorListBy",
      getReportDateRange: "moduleReport/getReportDateRange",
      paymentMethodList: "moduleUser/getPaymentMethodList",
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
      getLabExpenseList: "moduleLab/getLabExpenseList",
      getTreatList: "moduleAppointment/getTreatList",
      getTreatCateList: "moduleAppointment/getTreatCateList",
      getItemList: "moduleAppointment/getItemList",
      getItemCateList: "moduleAppointment/getItemCateList",
      branchInfo: "moduleBranch/getBranchInfo",
      clinicInfo: "moduleBranch/getClinicInfo",
    }),
    selectedDoctorName() {
      if (!this.selectedDoctor) return "";
      const doctor = this.getDoctorListBy(this.selectedDoctor, "uid");
      return doctor && doctor.fullName ? doctor.fullName : "";
    },
    allCatesSelected() {
      const treats = this.getTreatCateList || [];
      const items = this.getItemCateList || [];
      if (treats.length === 0 && items.length === 0) return false;
      return treats.every(c => this.selectedCateIds.includes(c.id))
        && items.every(c => this.selectedItemCateIds.includes(c.id))
        && this.includeOrthoPkg;
    },
    treatCateByItemId() {
      const map = new Map();
      for (const t of this.getTreatList || []) {
        if (t.id != null) map.set(t.id, t.treatCateId);
      }
      return map;
    },
    itemCateByDrugItemId() {
      const map = new Map();
      for (const it of this.getItemList || []) {
        if (it.id != null) map.set(it.id, it.cateId);
      }
      return map;
    },
    filteredPersonalIncomeList() {
      let list = this.personalIncomeList;
      const hasTreat = this.selectedCateIds.length > 0;
      const hasOrtho = this.includeOrthoPkg;
      const hasItem = this.selectedItemCateIds.length > 0;
      if (hasTreat || hasOrtho || hasItem) {
        const allowedTreat = new Set(this.selectedCateIds);
        const allowedItem = new Set(this.selectedItemCateIds);
        list = list.filter(i => {
          if (i.treatType === 2) return hasOrtho;
          if (i.treatType === 3) {
            return hasItem
              && allowedItem.has(this.itemCateByDrugItemId.get(parseInt(i.drugItemId)));
          }
          return hasTreat
            && allowedTreat.has(this.treatCateByItemId.get(i.txItemId));
        });
      }
      return list
        .slice(0)
        .sort(
          (a, b) =>
            moment(a.creationDt).valueOf() - moment(b.creationDt).valueOf()
        )
        .map((i, index) => {
          const paymentByMethod = (i.paymentAllocations || []).reduce(
            (acc, a) => {
              acc[a.id] = (acc[a.id] || 0) + parseFloat(a.amount);
              return acc;
            },
            {}
          );
          return { ...i, row: index + 1, paymentByMethod };
        });
    },

    today() {
      let today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
    dateFrom() {
      return this.dateRange[0]
        ? moment(this.dateRange[0]).startOf("day").format()
        : "";
    },
    dateTo() {
      return this.dateRange[1]
        ? moment(this.dateRange[1]).endOf("day").format()
        : "";
    },
    dateFromUTC() {
      return this.dateRange[0]
        ? moment(this.dateRange[0]).startOf("day").utc(true).format()
        : "";
    },
    dateToUTC() {
      return this.dateRange[1]
        ? moment(this.dateRange[1]).endOf("day").utc(true).format()
        : "";
    },
    rows() {
      return this.filteredPersonalIncomeList.length;
    },
    tableHeight() {
      if (this.isFullscreen) return "100%";
      return `calc(100vh - ${this.tableTopPos + 40}px)`;
    },
    incomeDistinctMethods() {
      const map = new Map();
      for (const r of this.filteredPersonalIncomeList) {
        for (const a of r.paymentAllocations || []) {
          if (!map.has(a.id)) map.set(a.id, { id: a.id, name: a.name });
        }
      }
      return [...map.values()].sort((a, b) => a.id - b.id);
    },
    hasAnyFee() {
      return this.filteredPersonalIncomeList.some(
        r => parseFloat(r.processingFeeBaht) > 0
      );
    },
    incomeFinancialFields() {
      const fields = [];
      if (this.hasAnyFee) {
        fields.push({
          key: "processingFeeBaht",
          label: "ค่าธรรมเนียม",
          class: "text-right",
          thClass: "narrow-spacing",
        });
      }
      fields.push({ key: "paidDf", label: "DF", class: "text-right" });
      if (this.reportSetting.cfVisible) {
        fields.push({ key: "paidCf", label: "CF", class: "text-right" });
      }
      return fields;
    },
    incomePaymentFields() {
      return this.incomeDistinctMethods.map(m => ({
        key: `paymentByMethod.${m.id}`,
        label: m.name,
        class: "text-right",
        formatter: v => this.formatNumber(v || 0),
      }));
    },
    incomeVisibleFields() {
      const middle =
        this.incomeViewMode === "payment"
          ? this.incomePaymentFields
          : this.incomeFinancialFields;
      return [...this.baseFields, ...middle];
    },
    isAfterCutoff() {
      if (!this.dateRange || this.dateRange.length < 1) return false;
      return moment(this.dateRange[0])
        .startOf("day")
        .isSameOrAfter(moment(this.cutoffDate));
    },
    deductDfAmount() {
      if (!this.reportSetting.isDeducted) return 0;
      const base = this.isAfterCutoff
        ? this.dfByPaymentSummary.netDf - this.total.labDf
        : this.total.paidDf - this.total.labDf;
      return +parseFloat((base * 10) / 100).toFixed(2);
    },
    netDf() {
      const dfBase = this.isAfterCutoff
        ? this.dfByPaymentSummary.netDf
        : this.total.paidDf;
      return dfBase - this.total.labDf - this.deductDfAmount;
    },
    incomeRowCount() {
      return this.filteredPersonalIncomeList.length;
    },
    labRowCount() {
      return this.labReport ? this.labReport.length : 0;
    },
    paymentRowCount() {
      return this.summaryByPayment ? this.summaryByPayment.length : 0;
    },
    workingDays() {
      const days = new Set(
        this.filteredPersonalIncomeList
          .map(r => r.creationDt && moment(r.creationDt).format("YYYY-MM-DD"))
          .filter(Boolean)
      );
      return days.size;
    },
    uniquePatientCount() {
      return new Set(
        this.filteredPersonalIncomeList.map(r => r.dn).filter(Boolean)
      ).size;
    },
    avgPerDay() {
      if (!this.workingDays) return 0;
      return this.netDf / this.workingDays;
    },
    byPaymentBars() {
      const list = this.summaryByPayment || [];
      const max = list.reduce((m, p) => Math.max(m, p.df || 0), 0) || 1;
      return list.map(p => ({
        name: p.name,
        df: p.df || 0,
        feeAmount: p.feeAmount || 0,
        pct: ((p.df || 0) / max) * 100,
      }));
    },
    // Check if date range crosses 1 Jan 2026 cutoff
    isCrossDateRange() {
      if (!this.dateRange || this.dateRange.length < 2) return false;
      const cutoff = moment("2026-02-01").startOf("day");
      const start = moment(this.dateRange[0]).startOf("day");
      const end = moment(this.dateRange[1]).startOf("day");
      return start.isBefore(cutoff) && end.isSameOrAfter(cutoff);
    },
  },
  watch: {},
  methods: {
    ...mapActions({
      fetchReportDoctorReceiptItem: "moduleReport/fetchReportDoctorReceiptItem",
      setStateReportDateRange: "moduleReport/setStateReportDateRange",
      fetchPaymentMethodList: "moduleUser/fetchPaymentMethodList",
      fetchLabExpenseForReport: "moduleLab/fetchLabExpenseForReport",
    }),
    init() {
      this.fetchPaymentMethodList({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      }).then(() => {
        this.feeMethods = this.paymentMethodList.filter(i => i.processingFee);
      });
      // treatList + treatCateList preloaded by router (requiresDataFetch).
    },
    moment() {
      return moment();
    },
    formatDate(date) {
      if (date) {
        return moment(date).locale("th").format("D MMM YY");
      } else {
        return "";
      }
    },
    formatNumber(num) {
      if (num === null || num === undefined || isNaN(num)) return "-";
      return Number(num).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatType(code) {
      switch (code) {
        case 1:
          return "รักษาทั่วไป";
        case 2:
          return "แพ็กเกจจัดฟัน";
        case 3:
          return "ยา และอุปกรณ์";
      }
    },

    showDialogToast(variant, textDetail) {
      this.$refs.Dialog.showToast(variant, textDetail);
    },
    toggleAllCates() {
      if (this.allCatesSelected) {
        this.selectedCateIds = [];
        this.selectedItemCateIds = [];
        this.includeOrthoPkg = false;
      } else {
        this.selectedCateIds = (this.getTreatCateList || []).map(c => c.id);
        this.selectedItemCateIds = (this.getItemCateList || []).map(c => c.id);
        this.includeOrthoPkg = true;
      }
    },
    sum(arg) {
      let num = this.filteredPersonalIncomeList.reduce((accum, item) => {
        return accum + parseFloat(item[arg]);
      }, 0.0);
      return Math.round(num * 100) / 100;
    },
    sendEmail() {},
    async fetchReport() {
      this.isLoading = true;
      this.setStateReportDateRange(this.dateRange);

      await this.fetchReportDoctorReceiptItem({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        actionUid: this.selectedDoctor,
      })
        .then(res => {
          this.personalIncomeList = res.data.report;
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async fetchLabReport() {
      await this.fetchLabExpenseForReport({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        event: {
          fromDfDate: this.dateRange[0] ? this.dateFromUTC : null,
          toDfDate: this.dateRange[1] ? this.dateToUTC : null,
          uid: this.selectedDoctor,
        },
      }).then(res => {
        this.labReport = res.data.filter(i => !i.cancelDt);
      });
    },
    buildExportSheets() {
      return this.tableList
        .filter(i => document.getElementById(i.id))
        .map(i => ({ tableId: i.id, name: i.sheetName }));
    },
    async excelExport() {
      this.perPage = null;
      await this.$nextTick();

      exportTableToExcel({
        reportTitle: this.reportTitle,
        reportNameEn: this.reportNameEn,
        dateFrom: this.dateRange[0],
        dateTo: this.dateRange[1],
        doctorName: this.selectedDoctorName,
        branchName: this.branchInfo && this.branchInfo.name,
        clinicName: this.clinicInfo && this.clinicInfo.name,
        sheets: this.buildExportSheets(),
      });

      this.perPage = 100;
    },
    async pdfExport() {
      this.perPage = null;
      await this.$nextTick();

      exportReportToPdf({
        reportTitle: this.reportTitle,
        reportNameEn: this.reportNameEn,
        dateFrom: this.dateRange[0],
        dateTo: this.dateRange[1],
        doctorName: this.selectedDoctorName,
        branchName: this.branchInfo && this.branchInfo.name,
        clinicName: this.clinicInfo && this.clinicInfo.name,
        sheets: this.buildExportSheets(),
      });

      this.perPage = 100;
    },
    notAfterToday(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },
    calSummaryTable() {
      this.total = {
        paid: this.calSum(this.personalIncomeList, "paid"),
        paidDf: this.calSum(this.personalIncomeList, "paidDf"),
        paidCf: this.calSum(this.personalIncomeList, "paidCf"),
        labTotal: this.calSum(this.labReport, "price"),
        labDf: this.calSum(this.labReport, "dfAmount"),
        labCf: this.calSum(this.labReport, "cfAmount"),
      };
    },
    calSum(list, key) {
      let num = list.reduce((accum, item) => {
        return accum + parseFloat(item[key]);
      }, 0);
      return Math.round(num * 100) / 100;
    },
    async fetchAll() {
      // Block cross-cutoff date range query
      if (this.isCrossDateRange) {
        this.showDialogToast(
          "warning",
          "ไม่สามารถ query ข้ามช่วง 1 ก.พ. 69 ได้ กรุณาเลือกช่วงวันที่ใหม่"
        );
        return;
      }

      await Promise.all([this.fetchReport(), this.fetchLabReport()]);

      // Fetch DF by payment type
      if (this.$refs.dfByPaymentType) {
        await this.$refs.dfByPaymentType.fetchReport();
        this.dfByPaymentSummary =
          this.$refs.dfByPaymentType.getSummary() || {};
        this.summaryByPayment =
          this.$refs.dfByPaymentType.getSummaryByPayment() || [];
      }

      this.calSummaryTable();
    },
    handleCfVisible() {
      // CF column visibility now lives in incomeFinancialFields (computed); just persist.
      localStorage.setItem("reportSetting", JSON.stringify(this.reportSetting));
    },
    incomeMethodTotal(methodId) {
      return this.filteredPersonalIncomeList.reduce(
        (acc, r) => acc + ((r.paymentByMethod && r.paymentByMethod[methodId]) || 0),
        0
      );
    },
    handleDeductChange(e) {
      this.reportSetting.isDeducted = e;
      localStorage.setItem("reportSetting", JSON.stringify(this.reportSetting));
    },
  },
  async mounted() {
    if (this.getReportDateRange[0] && this.getReportDateRange[1]) {
      this.dateRange = this.getReportDateRange;
    } else {
      this.dateRange = [this.today, this.today];
    }
    if (this.isAccessMenu(["DOCTOR"])) {
      this.selectedDoctor = this.userInfo.uid;
    }
    await this.$nextTick();

    this.tableTopPos = document
      .querySelector("#personalIncomeTable")
      .getBoundingClientRect().top;
    // Merge with defaults so Vue 2 keeps both keys reactive even when
    // localStorage only holds a partial object from an older session.
    this.reportSetting = {
      cfVisible: false,
      isDeducted: false,
      ...JSON.parse(localStorage.getItem("reportSetting") || "{}"),
    };
    this.handleCfVisible();

    this.handleExportReportPdf = payload => {
      if (this.$refs.pdfExport) this.$refs.pdfExport.generatePdf(payload);
    };
    eventBus.$on("exportReportPdf", this.handleExportReportPdf);
  },
  beforeDestroy() {
    if (this.handleExportReportPdf) {
      eventBus.$off("exportReportPdf", this.handleExportReportPdf);
    }
  },
  updated() {},
};
</script>

<style>
/* Fullscreen scroll fix — restore the vertical flex chain from .my-fullscreen
   down to .b-table-sticky-header. Outside fullscreen these rules don't apply
   so normal page layout is unchanged. */
.my-fullscreen .pir-workspace {
  flex: 1 1 0;
  min-height: 0;
  align-content: stretch;
}
.my-fullscreen .pir-main {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}
.my-fullscreen .pir-main > .pir-card {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.my-fullscreen .pir-main > .pir-card > .b-table {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
</style>
